/**
 * Прокручивает страницу до активного инпута при фокусе
 * Работает только в миниаппе на мобильных устройствах
 */
export const setupInputAutoScroll = () => {
    if (typeof window === "undefined") {
        return;
    }
    // СТРОГАЯ ПРОВЕРКА: только мобильные устройства с touch событиями
    // 1. Проверяем user agent - обязательно должно быть мобильное устройство
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    // Если это НЕ мобильное устройство по user agent - сразу выходим
    // Не полагаемся на размер экрана, так как окно браузера может быть узким
    if (!isMobileDevice) {
        return;
    }
    // 2. Дополнительная проверка: должно быть touch устройство
    // На десктопе даже с мобильным user agent не будет touch событий
    const hasTouchSupport = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (!hasTouchSupport) {
        return;
    }
    // 3. Проверяем, что это не десктоп с измененным user agent
    // Если ширина экрана большая (> 1024px), это скорее всего десктоп
    if (window.innerWidth > 1024 && window.screen.width > 1024) {
        return;
    }
    // Проверяем, что мы в миниаппе
    // Используем СТРОГУЮ проверку: только window.Telegram.WebApp (самый надежный способ)
    // И проверку на класс "miniapp" (добавляется в App.tsx только для миниаппа)
    // НЕ используем isTelegramMiniApp(), так как она может давать ложные срабатывания
    const hasTelegramWebApp = !!window.Telegram?.WebApp;
    const hasMiniappClass = document.body.classList.contains("miniapp") ||
        document.documentElement.classList.contains("miniapp");
    // Если нет ни Telegram WebApp, ни класса miniapp - выходим
    if (!hasTelegramWebApp && !hasMiniappClass) {
        return;
    }
    console.log("Input auto-scroll enabled for mini app on mobile device");
    const scrollToInput = (element) => {
        // Находим реальный input элемент (может быть внутри Mantine обертки)
        let inputElement = null;
        // Сначала проверяем сам элемент
        if (element.tagName === "INPUT" || element.tagName === "TEXTAREA") {
            inputElement = element;
        }
        // Проверяем классы Mantine
        else if (element.classList.contains("mantine-TextInput-input") ||
            element.classList.contains("mantine-Textarea-input") ||
            element.classList.contains("mantine-PasswordInput-input")) {
            inputElement = element;
        }
        // Ищем input внутри элемента (для Mantine оберток)
        else {
            inputElement = (element.querySelector("input") ||
                element.querySelector("textarea"));
        }
        // Если не нашли, пробуем через closest
        if (!inputElement) {
            inputElement = (element.closest("input") ||
                element.closest("textarea") ||
                element.closest(".mantine-TextInput-input") ||
                element.closest(".mantine-Textarea-input") ||
                element.closest(".mantine-PasswordInput-input"));
        }
        if (!inputElement) {
            console.warn("Input element not found for scrolling");
            return;
        }
        // Используем несколько попыток с разными задержками для надежности
        const attemptScroll = (delay) => {
            setTimeout(() => {
                try {
                    const rect = inputElement.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    // Предполагаем, что клавиатура занимает примерно 40% высоты экрана
                    const keyboardHeight = viewportHeight * 0.4;
                    const availableHeight = viewportHeight - keyboardHeight;
                    // Прокручиваем, если инпут находится ниже доступной области или слишком близко к низу
                    if (rect.bottom > availableHeight || rect.top < 100) {
                        // Используем scrollIntoView с блоком start для более предсказуемого поведения
                        inputElement.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                            inline: "nearest",
                        });
                        // Дополнительная корректировка через scrollBy
                        setTimeout(() => {
                            const newRect = inputElement.getBoundingClientRect();
                            const newViewportHeight = window.innerHeight;
                            const newKeyboardHeight = newViewportHeight * 0.4;
                            const newAvailableHeight = newViewportHeight - newKeyboardHeight;
                            if (newRect.bottom > newAvailableHeight) {
                                const scrollOffset = newRect.bottom - newAvailableHeight + 30;
                                window.scrollBy({
                                    top: scrollOffset,
                                    behavior: "smooth",
                                });
                            }
                        }, 200);
                    }
                }
                catch (error) {
                    console.error("Error scrolling to input:", error);
                }
            }, delay);
        };
        // Первая попытка через 100ms
        attemptScroll(100);
        // Вторая попытка через 300ms (когда клавиатура точно появилась)
        attemptScroll(300);
        // Третья попытка через 500ms (на случай медленной клавиатуры)
        attemptScroll(500);
    };
    const handleInputFocus = (event) => {
        const target = event.target;
        // Проверяем все возможные варианты инпутов
        const isInputElement = target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.classList.contains("mantine-TextInput-input") ||
            target.classList.contains("mantine-Textarea-input") ||
            target.classList.contains("mantine-PasswordInput-input") ||
            target.closest(".mantine-TextInput-input") !== null ||
            target.closest(".mantine-Textarea-input") !== null ||
            target.closest(".mantine-PasswordInput-input") !== null ||
            target.closest("input") !== null ||
            target.closest("textarea") !== null;
        if (isInputElement) {
            console.log("Input focused, scrolling to element:", target);
            scrollToInput(target);
        }
    };
    // Используем capture phase для более раннего перехвата события
    document.addEventListener("focusin", handleInputFocus, true);
    // Также добавляем обработчик на click для надежности
    const handleInputClick = (event) => {
        const target = event.target;
        const isInputElement = target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.closest("input") !== null ||
            target.closest("textarea") !== null ||
            target.closest(".mantine-TextInput-input") !== null ||
            target.closest(".mantine-Textarea-input") !== null ||
            target.closest(".mantine-PasswordInput-input") !== null;
        if (isInputElement) {
            // Небольшая задержка перед прокруткой
            setTimeout(() => {
                scrollToInput(target);
            }, 50);
        }
    };
    document.addEventListener("click", handleInputClick, true);
    // Возвращаем функцию для очистки
    return () => {
        document.removeEventListener("focusin", handleInputFocus, true);
        document.removeEventListener("click", handleInputClick, true);
    };
};
